import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { CrmMerchandiser } from '../crm.model';
import { CrmService } from '../crm.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-crm-merchandiser-view',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './merchandiser-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MerchandiserViewComponent implements OnInit {
  private crmService = inject(CrmService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);

  row = signal<CrmMerchandiser | null>(null);
  loading = signal(false);

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems = signal<MenuItem[]>([
    { label: 'CRM', routerLink: '/crm/dashboard' },
    { label: 'Merchandisers', routerLink: '/crm/merchandisers' }
  ]);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (Number.isFinite(id) && id > 0) {
      this.load(id);
    }
  }

  load(id: number): void {
    this.loading.set(true);
    this.crmService.getMerchandiser(id).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: response => {
        this.row.set(response.data || null);
        this.breadcrumbItems.set([
          { label: 'CRM', routerLink: '/crm/dashboard' },
          { label: 'Merchandisers', routerLink: '/crm/merchandisers' },
          { label: response.data?.name || `#${id}` }
        ]);
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Merchandisers', detail: 'Unable to load merchandiser.' })
    });
  }

  edit(): void {
    const id = this.row()?.id;
    if (id) this.router.navigate(['/crm/merchandisers', id, 'edit']);
  }

  back(): void {
    this.router.navigate(['/crm/merchandisers']);
  }
}
